import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './OrderCreation.css';

// Fixed pricing constants
const SPECIAL_NUMBERS_COST = 4.50; // Non-consecutive numbers cost per jersey

// Helper functions to create empty data structures
const createEmptyJersey = (id) => ({
  id,
  jersey_type: '',
  fabric_type: '',
  color: '',
  trim_type: '',
  trim_color: '',
  print_colors: [{ name: '' }],
  print_colors_free: false,
  // Print types
  print_type_front: '',
  print_type_sponsor: '',
  print_type_numbers: '',
  // Print colors by location
  team_print_color: '',
  sponsor_color: '',
  numbers_color: '',
  // Number placement
  numbers_front: false,
  numbers_back: true,
  // Youth options
  youth_sizes: { YXXS: '', YXS: '', YS: '', YM: '', YL: '', YXL: '' },
  youth_vendor_number: '',
  youth_sku: '',
  youth_quantity_price: '',
  youth_brand: '',
  youth_sponsor_front: false,
  youth_sponsor_back: false,
  youth_numbers_consecutive: false,
  youth_numbers_special: false,
  youth_consecutive_start: '',
  youth_consecutive_cost: '',
  youth_special_cost: '',
  youth_special_numbers: { YXXS: '', YXS: '', YS: '', YM: '', YL: '', YXL: '' },
  // Adult options
  adult_sizes: { AXS: '', AS: '', AM: '', AL: '', AXL: '', A2X: '', A3X: '' },
  adult_vendor_number: '',
  adult_sku: '',
  adult_quantity_price: '',
  adult_brand: '',
  adult_sponsor_front: false,
  adult_sponsor_back: false,
  adult_numbers_consecutive: false,
  adult_numbers_special: false,
  adult_consecutive_start: '',
  adult_consecutive_cost: '',
  adult_special_cost: '',
  adult_special_numbers: { AXS: '', AS: '', AM: '', AL: '', AXL: '', A2X: '', A3X: '' },
  // Coach options
  coach_sizes: { S: '', M: '', L: '', XL: '', '2X': '', '3X': '' },
  coach_vendor_number: '',
  coach_sku: '',
  coach_quantity_price: '',
  coach_brand: '',
  coach_sponsor_front: false,
  coach_sponsor_back: false,
  coach_numbers_consecutive: false,
  coach_numbers_special: false,
  coach_consecutive_start: '',
  coach_consecutive_cost: '',
  coach_special_cost: '',
  coach_special_numbers: { S: '', M: '', L: '', XL: '', '2X': '', '3X': '' },
  collapsed: false,
});

const createEmptyPantData = () => ({
  type: '',
  fabric: '',
  color: '',
  trim_type: '',
  trim_color: '',
  brand: '',
  youth_sizes: { YXS: '', YS: '', YM: '', YL: '', YXL: '' },
  youth_vendor_number: '',
  youth_sku: '',
  youth_quantity_price: '',
  adult_sizes: { AXS: '', AS: '', AM: '', AL: '', AXL: '', A2X: '', A3X: '' },
  adult_vendor_number: '',
  adult_sku: '',
  adult_quantity_price: '',
});

const createEmptyBeltData = () => ({
  // Youth belt
  youth_color: '',
  youth_brand: '',
  youth_vendor_number: '',
  youth_sku: '',
  youth_quantity: '',
  youth_quantity_price: '',
  // Adult belt
  adult_color: '',
  adult_brand: '',
  adult_vendor_number: '',
  adult_sku: '',
  adult_quantity: '',
  adult_quantity_price: '',
});

const SOCK_SIZE_LABELS = {
  'XS': 'XS (Youth 8-12)',
  'S': 'Small (Youth 12-5/Ladies 4-7)',
  '6-8': 'Medium (Men 6-8/Ladies 7-10)',
  '9-11': 'Large (Men 9-12/Ladies 10-13)',
  '10-13': 'XL (Mens 12-15/Ladies 13+)',
};

const createEmptySockData = () => ({
  sock_type: '',
  color: '',
  brand: '',
  sizes: {
    'XS': { quantity: '', vendor_number: '', sku: '', quantity_price: '' },
    'S': { quantity: '', vendor_number: '', sku: '', quantity_price: '' },
    '6-8': { quantity: '', vendor_number: '', sku: '', quantity_price: '' },
    '9-11': { quantity: '', vendor_number: '', sku: '', quantity_price: '' },
    '10-13': { quantity: '', vendor_number: '', sku: '', quantity_price: '' },
  },
});

const createEmptyCapData = () => ({
  cap_type: '',
  sizing: '',
  fabric: '',
  cap_color: '',
  crown: '',
  visor: '',
  eye: '',
  button: '',
  brand: '',
  youth_vendor_number: '',
  youth_sku: '',
  youth_quantity: '',
  youth_quantity_price: '',
  adult_vendor_number: '',
  adult_sku: '',
  adult_quantity: '',
  adult_quantity_price: '',
});

const createEmptyTeam = (id) => ({
  id,
  team_name: '',
  sponsor_name: '',
  collapsed: false,
  activeProductTab: 'jerseys',
  jerseys: [createEmptyJersey(1)],
  nextJerseyId: 2,
  pantData: createEmptyPantData(),
  beltData: createEmptyBeltData(),
  sockData: createEmptySockData(),
  capData: createEmptyCapData(),
});

const createEmptyDivision = (id) => ({
  id,
  name: '',
  collapsed: false,
  teams: [],
});

// Step labels for wizard
const STEPS = [
  { number: 1, label: 'League Info', icon: '1' },
  { number: 2, label: 'Structure', icon: '2' },
  { number: 3, label: 'Products', icon: '3' },
  { number: 4, label: 'Review', icon: '4' },
];

const OrderCreation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const customer = location.state?.customer;
  const editOrder = location.state?.editOrder;
  const intakeData = location.state?.intakeData;
  const isEditMode = !!editOrder;
  const isFromIntake = !!intakeData;

  // Wizard step state
  const [currentStep, setCurrentStep] = useState(1);

  // Initialize order data - check for intake data first
  const getInitialOrderData = () => {
    if (intakeData) {
      return {
        customer_name: intakeData.leagueName || '',
        customer_id: '',
        contact_name: intakeData.contactName || '',
        contact_phone: intakeData.contactPhone || '',
        quantity: String(intakeData.totalItems || ''),
        order_date: new Date().toISOString().split('T')[0],
        due_date: intakeData.requestedDate ? new Date(intakeData.requestedDate).toISOString().split('T')[0] : '',
        notes: intakeData.notes || '',
        status: 'Draft',
        intake_submission_id: intakeData.id,
        intake_submission_number: intakeData.submissionNumber,
      };
    }
    return {
      customer_name: customer?.cust_name || '',
      customer_id: customer?.id || '',
      contact_name: customer?.contact || '',
      contact_phone: customer?.phone || '',
      quantity: '',
      order_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: '',
      status: 'Pending',
    };
  };

  const [orderData, setOrderData] = useState(getInitialOrderData());

  // League search autocomplete state
  const [leagueSuggestions, setLeagueSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingLeagues, setSearchingLeagues] = useState(false);

  // Divisions state - main data structure
  const [divisions, setDivisions] = useState([]);
  const [nextDivisionId, setNextDivisionId] = useState(1);
  const [nextTeamId, setNextTeamId] = useState(1);

  // Track which team is being configured in Products step
  const [activeTeamConfig, setActiveTeamConfig] = useState(null);

  // Prevent scroll wheel from changing number input values
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: true });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  // Populate form when editing an existing order
  useEffect(() => {
    if (editOrder) {
      setOrderData({
        customer_name: editOrder.customer_name || '',
        customer_id: editOrder.customer_id || '',
        contact_name: editOrder.contact_name || '',
        contact_phone: editOrder.contact_phone || '',
        quantity: editOrder.quantity || '',
        order_date: editOrder.order_date ? new Date(editOrder.order_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        due_date: editOrder.due_date ? new Date(editOrder.due_date).toISOString().split('T')[0] : '',
        notes: editOrder.notes || '',
        status: editOrder.status || 'Pending',
      });

      // Populate divisions from order_details
      const details = editOrder.order_details;
      if (details && details.divisions && details.divisions.length > 0) {
        let maxDivisionId = 0;
        let maxTeamId = 0;

        const populatedDivisions = details.divisions.map((division, divIndex) => {
          const divId = divIndex + 1;
          maxDivisionId = Math.max(maxDivisionId, divId);

          const teams = (division.teams || []).map((team, teamIndex) => {
            const teamId = teamIndex + 1;
            maxTeamId = Math.max(maxTeamId, teamId);

            // Populate jerseys
            let maxJerseyId = 0;
            const jerseys = (team.jerseys || []).map((jersey, jerseyIndex) => {
              const jerseyId = jerseyIndex + 1;
              maxJerseyId = Math.max(maxJerseyId, jerseyId);
              return {
                id: jerseyId,
                jersey_type: jersey.jersey_type || '',
                fabric_type: jersey.fabric_type || '',
                color: jersey.color || '',
                trim_type: jersey.trim_type || '',
                trim_color: jersey.trim_color || '',
                print_colors: jersey.print_colors || [{ name: '' }],
                print_colors_free: jersey.print_colors_free || false,
                print_type_front: jersey.print_type_front || '',
                print_type_sponsor: jersey.print_type_sponsor || '',
                print_type_numbers: jersey.print_type_numbers || '',
                team_print_color: jersey.team_print_color || '',
                sponsor_color: jersey.sponsor_color || '',
                numbers_color: jersey.numbers_color || '',
                numbers_front: jersey.numbers_front || false,
                numbers_back: jersey.numbers_back !== false, // default true
                sponsor_front: jersey.sponsor_front || false,
                sponsor_back: jersey.sponsor_back || false,
                sponsor_youth: jersey.sponsor_youth || false,
                sponsor_adult: jersey.sponsor_adult || false,
                sponsor_coach: jersey.sponsor_coach || false,
                youth_sizes: jersey.youth?.sizes || { YXXS: '', YXS: '', YS: '', YM: '', YL: '', YXL: '' },
                youth_vendor_number: jersey.youth?.vendor_number || '',
                youth_sku: jersey.youth?.sku || '',
                youth_quantity_price: jersey.youth?.unit_price || '',
                youth_brand: jersey.youth?.brand || '',
                youth_sponsor_front: jersey.youth?.sponsor_front || false,
                youth_sponsor_back: jersey.youth?.sponsor_back || false,
                youth_numbers_consecutive: jersey.youth?.numbers_consecutive || false,
                youth_numbers_special: jersey.youth?.numbers_special || false,
                youth_consecutive_start: jersey.youth?.consecutive_start || '',
                youth_consecutive_cost: jersey.youth?.consecutive_cost || '',
                youth_special_cost: jersey.youth?.special_cost || '',
                youth_special_numbers: jersey.youth?.special_numbers || { YXXS: '', YXS: '', YS: '', YM: '', YL: '', YXL: '' },
                adult_sizes: jersey.adult?.sizes || { AXS: '', AS: '', AM: '', AL: '', AXL: '', A2X: '', A3X: '' },
                adult_vendor_number: jersey.adult?.vendor_number || '',
                adult_sku: jersey.adult?.sku || '',
                adult_quantity_price: jersey.adult?.unit_price || '',
                adult_brand: jersey.adult?.brand || '',
                adult_sponsor_front: jersey.adult?.sponsor_front || false,
                adult_sponsor_back: jersey.adult?.sponsor_back || false,
                adult_numbers_consecutive: jersey.adult?.numbers_consecutive || false,
                adult_numbers_special: jersey.adult?.numbers_special || false,
                adult_consecutive_start: jersey.adult?.consecutive_start || '',
                adult_consecutive_cost: jersey.adult?.consecutive_cost || '',
                adult_special_cost: jersey.adult?.special_cost || '',
                adult_special_numbers: jersey.adult?.special_numbers || { AXS: '', AS: '', AM: '', AL: '', AXL: '', A2X: '', A3X: '' },
                coach_sizes: jersey.coach?.sizes || { S: '', M: '', L: '', XL: '', '2X': '', '3X': '' },
                coach_vendor_number: jersey.coach?.vendor_number || '',
                coach_sku: jersey.coach?.sku || '',
                coach_quantity_price: jersey.coach?.unit_price || '',
                coach_brand: jersey.coach?.brand || '',
                coach_sponsor_front: jersey.coach?.sponsor_front || false,
                coach_sponsor_back: jersey.coach?.sponsor_back || false,
                coach_numbers_consecutive: jersey.coach?.numbers_consecutive || false,
                coach_numbers_special: jersey.coach?.numbers_special || false,
                coach_consecutive_start: jersey.coach?.consecutive_start || '',
                coach_consecutive_cost: jersey.coach?.consecutive_cost || '',
                coach_special_cost: jersey.coach?.special_cost || '',
                coach_special_numbers: jersey.coach?.special_numbers || { S: '', M: '', L: '', XL: '', '2X': '', '3X': '' },
                collapsed: false,
              };
            });

            return {
              id: teamId,
              team_name: team.team_name || '',
              sponsor_name: team.sponsor_name || '',
              collapsed: false,
              activeProductTab: 'jerseys',
              jerseys: jerseys.length > 0 ? jerseys : [createEmptyJersey(1)],
              nextJerseyId: maxJerseyId + 1,
              pantData: team.pants ? {
                type: team.pants.type || '',
                fabric: team.pants.fabric || '',
                color: team.pants.color || '',
                trim_type: team.pants.trim_type || '',
                trim_color: team.pants.trim_color || '',
                brand: team.pants.brand || '',
                youth_sizes: team.pants.youth?.sizes || { YXS: '', YS: '', YM: '', YL: '', YXL: '' },
                youth_vendor_number: team.pants.youth?.vendor_number || '',
                youth_sku: team.pants.youth?.sku || '',
                youth_quantity_price: team.pants.youth?.unit_price || '',
                adult_sizes: team.pants.adult?.sizes || { AXS: '', AS: '', AM: '', AL: '', AXL: '', A2X: '', A3X: '' },
                adult_vendor_number: team.pants.adult?.vendor_number || '',
                adult_sku: team.pants.adult?.sku || '',
                adult_quantity_price: team.pants.adult?.unit_price || '',
              } : createEmptyPantData(),
              beltData: team.belts ? {
                youth_color: team.belts.youth?.color || '',
                youth_brand: team.belts.youth?.brand || '',
                youth_vendor_number: team.belts.youth?.vendor_number || '',
                youth_sku: team.belts.youth?.sku || '',
                youth_quantity: team.belts.youth?.quantity || '',
                youth_quantity_price: team.belts.youth?.unit_price || '',
                adult_color: team.belts.adult?.color || '',
                adult_brand: team.belts.adult?.brand || '',
                adult_vendor_number: team.belts.adult?.vendor_number || '',
                adult_sku: team.belts.adult?.sku || '',
                adult_quantity: team.belts.adult?.quantity || '',
                adult_quantity_price: team.belts.adult?.unit_price || '',
              } : createEmptyBeltData(),
              sockData: team.socks ? {
                sock_type: team.socks.sock_type || '',
                color: team.socks.color || '',
                brand: team.socks.brand || '',
                sizes: {
                  'XS': {
                    quantity: team.socks.sizes?.['XS']?.quantity || '',
                    vendor_number: team.socks.sizes?.['XS']?.vendor_number || '',
                    sku: team.socks.sizes?.['XS']?.sku || '',
                    quantity_price: team.socks.sizes?.['XS']?.unit_price || '',
                  },
                  'S': {
                    quantity: team.socks.sizes?.['S']?.quantity || '',
                    vendor_number: team.socks.sizes?.['S']?.vendor_number || '',
                    sku: team.socks.sizes?.['S']?.sku || '',
                    quantity_price: team.socks.sizes?.['S']?.unit_price || '',
                  },
                  '6-8': {
                    quantity: team.socks.sizes?.['6-8']?.quantity || '',
                    vendor_number: team.socks.sizes?.['6-8']?.vendor_number || '',
                    sku: team.socks.sizes?.['6-8']?.sku || '',
                    quantity_price: team.socks.sizes?.['6-8']?.unit_price || '',
                  },
                  '9-11': {
                    quantity: team.socks.sizes?.['9-11']?.quantity || '',
                    vendor_number: team.socks.sizes?.['9-11']?.vendor_number || '',
                    sku: team.socks.sizes?.['9-11']?.sku || '',
                    quantity_price: team.socks.sizes?.['9-11']?.unit_price || '',
                  },
                  '10-13': {
                    quantity: team.socks.sizes?.['10-13']?.quantity || '',
                    vendor_number: team.socks.sizes?.['10-13']?.vendor_number || '',
                    sku: team.socks.sizes?.['10-13']?.sku || '',
                    quantity_price: team.socks.sizes?.['10-13']?.unit_price || '',
                  },
                },
              } : createEmptySockData(),
              capData: team.caps ? {
                cap_type: team.caps.cap_type || '',
                sizing: team.caps.sizing || '',
                fabric: team.caps.fabric || '',
                cap_color: team.caps.cap_color || '',
                crown: team.caps.crown || '',
                visor: team.caps.visor || '',
                eye: team.caps.eye || '',
                button: team.caps.button || '',
                brand: team.caps.brand || '',
                youth_vendor_number: team.caps.youth?.vendor_number || '',
                youth_sku: team.caps.youth?.sku || '',
                youth_quantity: team.caps.youth?.quantity || '',
                youth_quantity_price: team.caps.youth?.unit_price || '',
                adult_vendor_number: team.caps.adult?.vendor_number || '',
                adult_sku: team.caps.adult?.sku || '',
                adult_quantity: team.caps.adult?.quantity || '',
                adult_quantity_price: team.caps.adult?.unit_price || '',
              } : createEmptyCapData(),
            };
          });

          return {
            id: divId,
            name: division.name || '',
            collapsed: false,
            teams,
          };
        });

        setDivisions(populatedDivisions);
        setNextDivisionId(maxDivisionId + 1);
        setNextTeamId(maxTeamId + 1);
      }
    }
  }, [editOrder]);

  // Populate form from intake submission data
  useEffect(() => {
    if (intakeData && intakeData.orderDetails) {
      const details = intakeData.orderDetails;

      if (details.divisions && details.divisions.length > 0) {
        let maxDivisionId = 0;
        let maxTeamId = 0;

        const populatedDivisions = details.divisions.map((division, divIndex) => {
          const divId = divIndex + 1;
          maxDivisionId = Math.max(maxDivisionId, divId);

          const teams = (division.teams || []).map((team, teamIndex) => {
            const teamId = teamIndex + 1;
            maxTeamId = Math.max(maxTeamId, teamId);
            const teamKey = `${divIndex}-${teamIndex}`;

            // Get jersey sizes for this team from intake
            const jerseySizes = details.products?.jerseys?.teamSizes?.[teamKey] || {};
            const pantsSizes = details.products?.pants?.teamSizes?.[teamKey] || {};

            // Map intake sizes to the order format
            const mapSizesToYouthAdult = (sizes) => {
              const youth = { YXXS: '', YXS: '', YS: '', YM: '', YL: '', YXL: '' };
              const adult = { AXS: '', AS: '', AM: '', AL: '', AXL: '', A2X: '', A3X: '' };

              // Map common size labels
              if (sizes['XS']) adult['AXS'] = String(sizes['XS']);
              if (sizes['S']) adult['AS'] = String(sizes['S']);
              if (sizes['M']) adult['AM'] = String(sizes['M']);
              if (sizes['L']) adult['AL'] = String(sizes['L']);
              if (sizes['XL']) adult['AXL'] = String(sizes['XL']);
              if (sizes['2XL']) adult['A2X'] = String(sizes['2XL']);
              if (sizes['3XL']) adult['A3X'] = String(sizes['3XL']);

              return { youth, adult };
            };

            const jerseyMapped = mapSizesToYouthAdult(jerseySizes);
            const pantsMapped = mapSizesToYouthAdult(pantsSizes);

            // Create a jersey entry if there are jersey sizes
            const hasJerseySizes = Object.values(jerseySizes).some(v => parseInt(v) > 0);
            const jerseys = hasJerseySizes ? [{
              ...createEmptyJersey(1),
              jersey_type: details.products?.jerseys?.type || '',
              adult_sizes: jerseyMapped.adult,
              youth_sizes: jerseyMapped.youth,
              adult_sku: details.products?.jerseys?.sku || '',
            }] : [createEmptyJersey(1)];

            return {
              id: teamId,
              name: team.name || `Team ${teamIndex + 1}`,
              color: team.color || '',
              collapsed: false,
              jerseys,
              nextJerseyId: 2,
              pants: {
                ...createEmptyPantData(),
                type: details.products?.pants?.type || '',
                adult_sizes: pantsMapped.adult,
                youth_sizes: pantsMapped.youth,
                adult_sku: details.products?.pants?.sku || '',
              },
              belts: createEmptyBeltData(),
              socks: createEmptySockData(),
              caps: {
                ...createEmptyCapData(),
                type: details.products?.caps?.type || '',
                adult_sku: details.products?.caps?.sku || '',
              },
            };
          });

          return {
            id: divId,
            name: division.name || '',
            collapsed: false,
            teams,
          };
        });

        setDivisions(populatedDivisions);
        setNextDivisionId(maxDivisionId + 1);
        setNextTeamId(maxTeamId + 1);
      }
    }
  }, [intakeData]);

  // Search leagues as user types
  const searchLeagues = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setLeagueSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSearchingLeagues(true);
    try {
      const response = await api.get('/league-uniform-customers', {
        params: { search: searchTerm, limit: 10 }
      });
      setLeagueSuggestions(response.data.customers || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Error searching leagues:', err);
      setLeagueSuggestions([]);
    } finally {
      setSearchingLeagues(false);
    }
  };

  const handleLeagueSelect = (league) => {
    setOrderData((prev) => ({
      ...prev,
      customer_name: league.cust_name || '',
      customer_id: league.id || '',
      contact_name: league.contact || '',
      contact_phone: league.phone || '',
    }));
    setShowSuggestions(false);
    setLeagueSuggestions([]);
  };

  const handleLeagueNameChange = (e) => {
    const { value } = e.target;
    setOrderData((prev) => ({
      ...prev,
      customer_name: value,
      customer_id: '',
    }));
    searchLeagues(value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Division management functions
  const addDivision = () => {
    const newDivision = createEmptyDivision(nextDivisionId);
    setDivisions([...divisions, newDivision]);
    setNextDivisionId(nextDivisionId + 1);
  };

  const removeDivision = (divisionId) => {
    setDivisions(divisions.filter(d => d.id !== divisionId));
  };

  const toggleDivisionCollapse = (divisionId) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? { ...d, collapsed: !d.collapsed } : d
    ));
  };

  const updateDivisionName = (divisionId, name) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? { ...d, name } : d
    ));
  };

  // Team management functions
  const addTeam = (divisionId) => {
    const newTeam = createEmptyTeam(nextTeamId);
    setDivisions(divisions.map(d =>
      d.id === divisionId ? { ...d, teams: [...d.teams, newTeam] } : d
    ));
    setNextTeamId(nextTeamId + 1);
  };

  const removeTeam = (divisionId, teamId) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? { ...d, teams: d.teams.filter(t => t.id !== teamId) } : d
    ));
  };

  const updateTeamField = (divisionId, teamId, field, value) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? { ...t, [field]: value } : t
        )
      } : d
    ));
  };

  const setActiveProductTab = (divisionId, teamId, tab) => {
    updateTeamField(divisionId, teamId, 'activeProductTab', tab);
  };

  // Jersey management functions (within a team)
  const addJersey = (divisionId, teamId) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            jerseys: [...t.jerseys, createEmptyJersey(t.nextJerseyId)],
            nextJerseyId: t.nextJerseyId + 1
          } : t
        )
      } : d
    ));
  };

  const removeJersey = (divisionId, teamId, jerseyId) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            jerseys: t.jerseys.filter(j => j.id !== jerseyId)
          } : t
        )
      } : d
    ));
  };

  const toggleJerseyCollapse = (divisionId, teamId, jerseyId) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            jerseys: t.jerseys.map(j =>
              j.id === jerseyId ? { ...j, collapsed: !j.collapsed } : j
            )
          } : t
        )
      } : d
    ));
  };

  const handleJerseyBasicChange = (divisionId, teamId, jerseyId, e) => {
    const { name, value } = e.target;
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            jerseys: t.jerseys.map(j =>
              j.id === jerseyId ? { ...j, [name]: value } : j
            )
          } : t
        )
      } : d
    ));
  };

  const handleJerseySponsorChange = (divisionId, teamId, jerseyId, field, checked) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            jerseys: t.jerseys.map(j =>
              j.id === jerseyId ? { ...j, [field]: checked } : j
            )
          } : t
        )
      } : d
    ));
  };

  // Print color handlers
  const handlePrintColorCountChange = (divisionId, teamId, jerseyId, count) => {
    // Max 2 colors allowed
    const clampedCount = Math.min(Math.max(count, 1), 2);
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            jerseys: t.jerseys.map(j => {
              if (j.id !== jerseyId) return j;
              const currentColors = j.print_colors || [{ name: '' }];
              let newColors;
              if (clampedCount > currentColors.length) {
                // Add more colors
                newColors = [...currentColors];
                while (newColors.length < clampedCount) {
                  newColors.push({ name: '' });
                }
              } else {
                // Trim colors
                newColors = currentColors.slice(0, clampedCount);
              }
              return { ...j, print_colors: newColors };
            })
          } : t
        )
      } : d
    ));
  };

  const handlePrintColorChange = (divisionId, teamId, jerseyId, index, field, value) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            jerseys: t.jerseys.map(j => {
              if (j.id !== jerseyId) return j;
              const newColors = [...(j.print_colors || [{ name: '' }])];
              newColors[index] = { ...newColors[index], [field]: value };
              return { ...j, print_colors: newColors };
            })
          } : t
        )
      } : d
    ));
  };

  const handleJerseySizeChange = (divisionId, teamId, jerseyId, category, size, value) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            jerseys: t.jerseys.map(j =>
              j.id === jerseyId ? {
                ...j,
                [category]: { ...(j[category] || {}), [size]: value }
              } : j
            )
          } : t
        )
      } : d
    ));
  };

  // Product data handlers (within a team)
  const handlePantBasicChange = (divisionId, teamId, e) => {
    const { name, value } = e.target;
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            pantData: { ...t.pantData, [name]: value }
          } : t
        )
      } : d
    ));
  };

  const handlePantSizeChange = (divisionId, teamId, category, size, value) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            pantData: {
              ...t.pantData,
              [category]: { ...t.pantData[category], [size]: value }
            }
          } : t
        )
      } : d
    ));
  };

  const handleBeltChange = (divisionId, teamId, e) => {
    const { name, value } = e.target;
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            beltData: { ...t.beltData, [name]: value }
          } : t
        )
      } : d
    ));
  };

  const handleSockBasicChange = (divisionId, teamId, e) => {
    const { name, value } = e.target;
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            sockData: { ...t.sockData, [name]: value }
          } : t
        )
      } : d
    ));
  };

  const handleSockSizeChange = (divisionId, teamId, size, field, value) => {
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            sockData: {
              ...t.sockData,
              sizes: {
                ...t.sockData.sizes,
                [size]: { ...t.sockData.sizes[size], [field]: value }
              }
            }
          } : t
        )
      } : d
    ));
  };

  const handleCapBasicChange = (divisionId, teamId, e) => {
    const { name, value } = e.target;
    setDivisions(divisions.map(d =>
      d.id === divisionId ? {
        ...d,
        teams: d.teams.map(t =>
          t.id === teamId ? {
            ...t,
            capData: { ...t.capData, [name]: value }
          } : t
        )
      } : d
    ));
  };

  // Calculation helpers

  // Calculate print color cost per jersey (fixed pricing: 1 color = $3.25, 2 colors = $4.50)
  // Can be overridden to $0 via jersey.print_colors_free (e.g. included/promotional pricing)
  const calculatePrintColorCostPerJersey = (jersey) => {
    if (jersey.print_colors_free) return 0;
    const colorCount = (jersey.print_colors || []).length;
    if (colorCount >= 2) return 4.50;
    return 3.25; // 1 color (minimum)
  };

  // Count special numbers from comma-separated string
  const countSpecialNumbers = (specialNumbersObj) => {
    return Object.values(specialNumbersObj).reduce((total, str) => {
      if (!str || !str.trim()) return total;
      // Split by comma and count non-empty entries
      const numbers = str.split(',').map(n => n.trim()).filter(n => n !== '');
      return total + numbers.length;
    }, 0);
  };

  // Calculate numbering costs for a category
  const calculateNumberingCost = (totalQty, specialNumbersObj, consecutiveEnabled, consecutiveStart, consecutiveCost, specialEnabled) => {
    const specialCount = specialEnabled ? countSpecialNumbers(specialNumbersObj) : 0;
    const consecutiveCount = (consecutiveEnabled && consecutiveStart) ? Math.max(0, totalQty - specialCount) : 0;
    const consecutiveTotal = consecutiveCount * (parseFloat(consecutiveCost) || 0);
    const specialTotal = specialCount * SPECIAL_NUMBERS_COST; // Fixed $4.50/jersey
    return { consecutiveCount, specialCount, consecutiveTotal, specialTotal, total: consecutiveTotal + specialTotal };
  };

  const calculateYouthJerseyQty = (jersey) => {
    return Object.values(jersey.youth_sizes).reduce((total, qty) => total + (parseInt(qty) || 0), 0);
  };

  const calculateYouthNumberingCost = (jersey) => {
    const totalQty = calculateYouthJerseyQty(jersey);
    return calculateNumberingCost(
      totalQty,
      jersey.youth_special_numbers || {},
      jersey.youth_numbers_consecutive,
      jersey.youth_consecutive_start,
      jersey.youth_consecutive_cost,
      jersey.youth_numbers_special
    );
  };

  const calculateYouthJerseyTotal = (jersey) => {
    const totalQty = calculateYouthJerseyQty(jersey);
    const price = parseFloat(jersey.youth_quantity_price) || 0;
    let total = totalQty * price;

    // Add print color cost for youth
    const printColorCost = calculatePrintColorCostPerJersey(jersey);
    total += totalQty * printColorCost;

    // Add sponsor cost for youth
    let sponsorPrice = 0;
    if (jersey.youth_sponsor_front) sponsorPrice += 3.50;
    if (jersey.youth_sponsor_back) sponsorPrice += 3.50;
    total += totalQty * sponsorPrice;

    // Add numbering cost for youth
    const numberingCost = calculateYouthNumberingCost(jersey);
    total += numberingCost.total;

    return total.toFixed(2);
  };

  const calculateYouthSponsorTotal = (jersey) => {
    const totalQty = calculateYouthJerseyQty(jersey);
    let sponsorPrice = 0;
    if (jersey.youth_sponsor_front) sponsorPrice += 3.50;
    if (jersey.youth_sponsor_back) sponsorPrice += 3.50;
    return (totalQty * sponsorPrice).toFixed(2);
  };

  // Calculate total sponsor pricing for a jersey (all categories combined)
  const calculateSponsorTotal = (jersey) => {
    const youthSponsor = parseFloat(calculateYouthSponsorTotal(jersey)) || 0;
    const adultSponsor = parseFloat(calculateAdultSponsorTotal(jersey)) || 0;
    const coachSponsor = parseFloat(calculateCoachSponsorTotal(jersey)) || 0;
    return (youthSponsor + adultSponsor + coachSponsor).toFixed(2);
  };

  // Get sponsor placement text for a category
  const getSponsorPlacementText = (front, back) => {
    if (front && back) return 'Front & Back';
    if (front) return 'Front';
    if (back) return 'Back';
    return 'None';
  };

  const calculateAdultJerseyQty = (jersey) => {
    return Object.values(jersey.adult_sizes).reduce((total, qty) => total + (parseInt(qty) || 0), 0);
  };

  const calculateAdultNumberingCost = (jersey) => {
    const totalQty = calculateAdultJerseyQty(jersey);
    return calculateNumberingCost(
      totalQty,
      jersey.adult_special_numbers || {},
      jersey.adult_numbers_consecutive,
      jersey.adult_consecutive_start,
      jersey.adult_consecutive_cost,
      jersey.adult_numbers_special
    );
  };

  const calculateAdultJerseyTotal = (jersey) => {
    const totalQty = calculateAdultJerseyQty(jersey);
    const price = parseFloat(jersey.adult_quantity_price) || 0;
    let total = totalQty * price;

    // Add print color cost for adult
    const printColorCost = calculatePrintColorCostPerJersey(jersey);
    total += totalQty * printColorCost;

    // Add sponsor cost for adult
    let sponsorPrice = 0;
    if (jersey.adult_sponsor_front) sponsorPrice += 3.50;
    if (jersey.adult_sponsor_back) sponsorPrice += 3.50;
    total += totalQty * sponsorPrice;

    // Add numbering cost for adult
    const numberingCost = calculateAdultNumberingCost(jersey);
    total += numberingCost.total;

    return total.toFixed(2);
  };

  const calculateAdultSponsorTotal = (jersey) => {
    const totalQty = calculateAdultJerseyQty(jersey);
    let sponsorPrice = 0;
    if (jersey.adult_sponsor_front) sponsorPrice += 3.50;
    if (jersey.adult_sponsor_back) sponsorPrice += 3.50;
    return (totalQty * sponsorPrice).toFixed(2);
  };

  const calculateCoachJerseyQty = (jersey) => {
    return Object.values(jersey.coach_sizes).reduce((total, qty) => total + (parseInt(qty) || 0), 0);
  };

  const calculateCoachNumberingCost = (jersey) => {
    const totalQty = calculateCoachJerseyQty(jersey);
    return calculateNumberingCost(
      totalQty,
      jersey.coach_special_numbers || {},
      jersey.coach_numbers_consecutive,
      jersey.coach_consecutive_start,
      jersey.coach_consecutive_cost,
      jersey.coach_numbers_special
    );
  };

  const calculateCoachJerseyTotal = (jersey) => {
    const totalQty = calculateCoachJerseyQty(jersey);
    const price = parseFloat(jersey.coach_quantity_price) || 0;
    let total = totalQty * price;

    // Add print color cost for coach
    const printColorCost = calculatePrintColorCostPerJersey(jersey);
    total += totalQty * printColorCost;

    // Add sponsor cost for coach
    let sponsorPrice = 0;
    if (jersey.coach_sponsor_front) sponsorPrice += 3.50;
    if (jersey.coach_sponsor_back) sponsorPrice += 3.50;
    total += totalQty * sponsorPrice;

    // Add numbering cost for coach
    const numberingCost = calculateCoachNumberingCost(jersey);
    total += numberingCost.total;

    return total.toFixed(2);
  };

  const calculateCoachSponsorTotal = (jersey) => {
    const totalQty = calculateCoachJerseyQty(jersey);
    let sponsorPrice = 0;
    if (jersey.coach_sponsor_front) sponsorPrice += 3.50;
    if (jersey.coach_sponsor_back) sponsorPrice += 3.50;
    return (totalQty * sponsorPrice).toFixed(2);
  };

  const calculateYouthPantQty = (pantData) => {
    return Object.values(pantData.youth_sizes).reduce((total, qty) => total + (parseInt(qty) || 0), 0);
  };

  const calculateYouthPantTotal = (pantData) => {
    const totalQty = calculateYouthPantQty(pantData);
    const price = parseFloat(pantData.youth_quantity_price) || 0;
    return (totalQty * price).toFixed(2);
  };

  const calculateAdultPantQty = (pantData) => {
    return Object.values(pantData.adult_sizes).reduce((total, qty) => total + (parseInt(qty) || 0), 0);
  };

  const calculateAdultPantTotal = (pantData) => {
    const totalQty = calculateAdultPantQty(pantData);
    const price = parseFloat(pantData.adult_quantity_price) || 0;
    return (totalQty * price).toFixed(2);
  };

  const calculateSockTotal = (sockData) => {
    return Object.values(sockData.sizes).reduce((total, size) => total + (parseInt(size.quantity) || 0), 0);
  };

  const calculateYouthCapTotal = (capData) => {
    const qty = parseInt(capData.youth_quantity) || 0;
    const price = parseFloat(capData.youth_quantity_price) || 0;
    return (qty * price).toFixed(2);
  };

  const calculateAdultCapTotal = (capData) => {
    const qty = parseInt(capData.adult_quantity) || 0;
    const price = parseFloat(capData.adult_quantity_price) || 0;
    return (qty * price).toFixed(2);
  };

  // Calculate order totals for review
  const calculateOrderTotals = () => {
    let totalQuantity = 0;
    let totalPrice = 0;

    divisions.forEach(division => {
      division.teams.forEach(team => {
        // Jerseys
        team.jerseys.forEach(jersey => {
          const youthQty = calculateYouthJerseyQty(jersey);
          const adultQty = calculateAdultJerseyQty(jersey);
          const coachQty = calculateCoachJerseyQty(jersey);
          totalQuantity += youthQty + adultQty + coachQty;
          // Sponsor costs are now included in each category's total
          totalPrice += parseFloat(calculateYouthJerseyTotal(jersey)) || 0;
          totalPrice += parseFloat(calculateAdultJerseyTotal(jersey)) || 0;
          totalPrice += parseFloat(calculateCoachJerseyTotal(jersey)) || 0;
        });

        // Pants
        const youthPantQty = calculateYouthPantQty(team.pantData);
        const adultPantQty = calculateAdultPantQty(team.pantData);
        totalQuantity += youthPantQty + adultPantQty;
        totalPrice += parseFloat(calculateYouthPantTotal(team.pantData)) || 0;
        totalPrice += parseFloat(calculateAdultPantTotal(team.pantData)) || 0;

        // Belts
        const youthBeltQty = parseInt(team.beltData.youth_quantity) || 0;
        const adultBeltQty = parseInt(team.beltData.adult_quantity) || 0;
        totalQuantity += youthBeltQty + adultBeltQty;
        totalPrice += youthBeltQty * (parseFloat(team.beltData.youth_quantity_price) || 0);
        totalPrice += adultBeltQty * (parseFloat(team.beltData.adult_quantity_price) || 0);

        // Socks
        totalQuantity += calculateSockTotal(team.sockData);
        Object.values(team.sockData.sizes).forEach(size => {
          const qty = parseInt(size.quantity) || 0;
          const price = parseFloat(size.quantity_price) || 0;
          totalPrice += qty * price;
        });

        // Caps
        const youthCapQty = parseInt(team.capData.youth_quantity) || 0;
        const adultCapQty = parseInt(team.capData.adult_quantity) || 0;
        totalQuantity += youthCapQty + adultCapQty;
        totalPrice += parseFloat(calculateYouthCapTotal(team.capData)) || 0;
        totalPrice += parseFloat(calculateAdultCapTotal(team.capData)) || 0;
      });
    });

    return { totalQuantity, totalPrice: totalPrice.toFixed(2) };
  };

  const handleSubmit = async () => {
    if (!orderData.customer_name.trim()) {
      alert('League name is required');
      return;
    }

    // Calculate totals and build order details
    let totalQuantity = 0;
    let totalPrice = 0;

    const divisionsDetails = divisions.map(division => {
      const teamsDetails = division.teams.map(team => {
        // Build jerseys
        const jerseysDetails = team.jerseys.map((jersey, index) => {
          const youthQty = calculateYouthJerseyQty(jersey);
          const youthTotal = parseFloat(calculateYouthJerseyTotal(jersey)) || 0;
          const adultQty = calculateAdultJerseyQty(jersey);
          const adultTotal = parseFloat(calculateAdultJerseyTotal(jersey)) || 0;
          const coachQty = calculateCoachJerseyQty(jersey);
          const coachTotal = parseFloat(calculateCoachJerseyTotal(jersey)) || 0;

          const sponsorTotal = parseFloat(calculateSponsorTotal(jersey)) || 0;
          totalQuantity += youthQty + adultQty + coachQty;
          // Sponsor costs are already included in each category's total
          totalPrice += youthTotal + adultTotal + coachTotal;

          return {
            jersey_number: index + 1,
            jersey_type: jersey.jersey_type,
            fabric_type: jersey.fabric_type,
            color: jersey.color,
            trim_type: jersey.trim_type,
            trim_color: jersey.trim_color,
            print_colors: jersey.print_colors || [{ name: '' }],
            print_colors_free: jersey.print_colors_free || false,
            print_type_front: jersey.print_type_front,
            print_type_sponsor: jersey.print_type_sponsor,
            print_type_numbers: jersey.print_type_numbers,
            team_print_color: jersey.team_print_color,
            sponsor_color: jersey.sponsor_color,
            numbers_color: jersey.numbers_color,
            numbers_front: jersey.numbers_front,
            numbers_back: jersey.numbers_back,
            sponsor_front: jersey.sponsor_front,
            sponsor_back: jersey.sponsor_back,
            sponsor_youth: jersey.sponsor_youth,
            sponsor_adult: jersey.sponsor_adult,
            sponsor_coach: jersey.sponsor_coach,
            sponsor_total: sponsorTotal,
            youth: {
              sizes: jersey.youth_sizes,
              quantity: youthQty,
              vendor_number: jersey.youth_vendor_number,
              sku: jersey.youth_sku,
              unit_price: parseFloat(jersey.youth_quantity_price) || 0,
              total: youthTotal,
              brand: jersey.youth_brand,
              sponsor_front: jersey.youth_sponsor_front || false,
              sponsor_back: jersey.youth_sponsor_back || false,
              numbers_consecutive: jersey.youth_numbers_consecutive || false,
              numbers_special: jersey.youth_numbers_special || false,
              consecutive_start: jersey.youth_consecutive_start || '',
              consecutive_cost: jersey.youth_consecutive_cost || '',
              special_cost: jersey.youth_special_cost || '',
              special_numbers: jersey.youth_special_numbers || {},
            },
            adult: {
              sizes: jersey.adult_sizes,
              quantity: adultQty,
              vendor_number: jersey.adult_vendor_number,
              sku: jersey.adult_sku,
              unit_price: parseFloat(jersey.adult_quantity_price) || 0,
              total: adultTotal,
              brand: jersey.adult_brand,
              sponsor_front: jersey.adult_sponsor_front || false,
              sponsor_back: jersey.adult_sponsor_back || false,
              numbers_consecutive: jersey.adult_numbers_consecutive || false,
              numbers_special: jersey.adult_numbers_special || false,
              consecutive_start: jersey.adult_consecutive_start || '',
              consecutive_cost: jersey.adult_consecutive_cost || '',
              special_cost: jersey.adult_special_cost || '',
              special_numbers: jersey.adult_special_numbers || {},
            },
            coach: {
              sizes: jersey.coach_sizes,
              quantity: coachQty,
              vendor_number: jersey.coach_vendor_number,
              sku: jersey.coach_sku,
              unit_price: parseFloat(jersey.coach_quantity_price) || 0,
              total: coachTotal,
              brand: jersey.coach_brand,
              sponsor_front: jersey.coach_sponsor_front || false,
              sponsor_back: jersey.coach_sponsor_back || false,
              numbers_consecutive: jersey.coach_numbers_consecutive || false,
              numbers_special: jersey.coach_numbers_special || false,
              consecutive_start: jersey.coach_consecutive_start || '',
              consecutive_cost: jersey.coach_consecutive_cost || '',
              special_cost: jersey.coach_special_cost || '',
              special_numbers: jersey.coach_special_numbers || {},
            },
          };
        });

        // Build pants
        const youthPantQty = calculateYouthPantQty(team.pantData);
        const youthPantTotal = parseFloat(calculateYouthPantTotal(team.pantData)) || 0;
        const adultPantQty = calculateAdultPantQty(team.pantData);
        const adultPantTotal = parseFloat(calculateAdultPantTotal(team.pantData)) || 0;
        totalQuantity += youthPantQty + adultPantQty;
        totalPrice += youthPantTotal + adultPantTotal;

        const pantsDetails = {
          type: team.pantData.type,
          fabric: team.pantData.fabric,
          color: team.pantData.color,
          trim_type: team.pantData.trim_type,
          trim_color: team.pantData.trim_color,
          brand: team.pantData.brand,
          youth: {
            sizes: team.pantData.youth_sizes,
            quantity: youthPantQty,
            vendor_number: team.pantData.youth_vendor_number,
            sku: team.pantData.youth_sku,
            unit_price: parseFloat(team.pantData.youth_quantity_price) || 0,
            total: youthPantTotal,
          },
          adult: {
            sizes: team.pantData.adult_sizes,
            quantity: adultPantQty,
            vendor_number: team.pantData.adult_vendor_number,
            sku: team.pantData.adult_sku,
            unit_price: parseFloat(team.pantData.adult_quantity_price) || 0,
            total: adultPantTotal,
          },
        };

        // Build belts
        const youthBeltQty = parseInt(team.beltData.youth_quantity) || 0;
        const adultBeltQty = parseInt(team.beltData.adult_quantity) || 0;
        const youthBeltTotal = youthBeltQty * (parseFloat(team.beltData.youth_quantity_price) || 0);
        const adultBeltTotal = adultBeltQty * (parseFloat(team.beltData.adult_quantity_price) || 0);
        totalQuantity += youthBeltQty + adultBeltQty;
        totalPrice += youthBeltTotal + adultBeltTotal;

        const beltsDetails = {
          youth: {
            color: team.beltData.youth_color,
            brand: team.beltData.youth_brand,
            vendor_number: team.beltData.youth_vendor_number,
            sku: team.beltData.youth_sku,
            quantity: youthBeltQty,
            unit_price: parseFloat(team.beltData.youth_quantity_price) || 0,
            total: youthBeltTotal,
          },
          adult: {
            color: team.beltData.adult_color,
            brand: team.beltData.adult_brand,
            vendor_number: team.beltData.adult_vendor_number,
            sku: team.beltData.adult_sku,
            quantity: adultBeltQty,
            unit_price: parseFloat(team.beltData.adult_quantity_price) || 0,
            total: adultBeltTotal,
          },
          total: youthBeltTotal + adultBeltTotal,
        };

        // Build socks
        const sockSizesDetails = {};
        let sockTotalQty = 0;
        let sockTotalPrice = 0;
        Object.entries(team.sockData.sizes).forEach(([size, data]) => {
          const qty = parseInt(data.quantity) || 0;
          const price = parseFloat(data.quantity_price) || 0;
          const sizeTotal = qty * price;
          sockTotalQty += qty;
          sockTotalPrice += sizeTotal;
          sockSizesDetails[size] = {
            quantity: qty,
            vendor_number: data.vendor_number,
            sku: data.sku,
            unit_price: price,
            total: sizeTotal,
          };
        });
        totalQuantity += sockTotalQty;
        totalPrice += sockTotalPrice;

        const socksDetails = {
          sock_type: team.sockData.sock_type,
          color: team.sockData.color,
          brand: team.sockData.brand,
          sizes: sockSizesDetails,
          quantity: sockTotalQty,
          total: sockTotalPrice,
        };

        // Build caps
        const youthCapQty = parseInt(team.capData.youth_quantity) || 0;
        const youthCapTotal = parseFloat(calculateYouthCapTotal(team.capData)) || 0;
        const adultCapQty = parseInt(team.capData.adult_quantity) || 0;
        const adultCapTotal = parseFloat(calculateAdultCapTotal(team.capData)) || 0;
        totalQuantity += youthCapQty + adultCapQty;
        totalPrice += youthCapTotal + adultCapTotal;

        const capsDetails = {
          cap_type: team.capData.cap_type,
          sizing: team.capData.sizing,
          fabric: team.capData.fabric,
          cap_color: team.capData.cap_color,
          crown: team.capData.crown,
          visor: team.capData.visor,
          eye: team.capData.eye,
          button: team.capData.button,
          brand: team.capData.brand,
          youth: {
            quantity: youthCapQty,
            vendor_number: team.capData.youth_vendor_number,
            sku: team.capData.youth_sku,
            unit_price: parseFloat(team.capData.youth_quantity_price) || 0,
            total: youthCapTotal,
          },
          adult: {
            quantity: adultCapQty,
            vendor_number: team.capData.adult_vendor_number,
            sku: team.capData.adult_sku,
            unit_price: parseFloat(team.capData.adult_quantity_price) || 0,
            total: adultCapTotal,
          },
        };

        return {
          team_name: team.team_name,
          sponsor_name: team.sponsor_name,
          jerseys: jerseysDetails,
          pants: pantsDetails,
          belts: beltsDetails,
          socks: socksDetails,
          caps: capsDetails,
        };
      });

      return {
        name: division.name,
        teams: teamsDetails,
      };
    });

    const orderDetails = { divisions: divisionsDetails };

    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const orderNumber = `ORD-${dateStr}-${randomNum}`;

    try {
      const orderPayload = {
        customer_name: orderData.customer_name,
        contact_name: orderData.contact_name,
        contact_phone: orderData.contact_phone,
        quantity: totalQuantity || 0,
        status: orderData.status,
        order_date: orderData.order_date,
        due_date: orderData.due_date || null,
        notes: orderData.notes || null,
        order_details: orderDetails,
        total_price: totalPrice,
      };

      if (isEditMode) {
        const response = await api.put(`/uniform-orders/orders/${editOrder.id}`, {
          order_number: editOrder.order_number,
          ...orderPayload,
        });

        if (response.data.order) {
          alert('Order updated successfully!');
          navigate('/league-uniform-management');
        }
      } else {
        const response = await api.post('/uniform-orders/orders', {
          order_number: orderNumber,
          ...orderPayload,
        });

        if (response.data.order) {
          alert('Order created successfully!');
          navigate('/league-uniform-management');
        }
      }

      if (orderData.customer_id && (orderData.contact_name || orderData.contact_phone)) {
        try {
          await api.put(`/league-uniform-customers/${orderData.customer_id}`, {
            contact: orderData.contact_name,
            phone: orderData.contact_phone,
          });
        } catch (updateErr) {
          console.log('Note: Could not update league contact info', updateErr);
        }
      }
    } catch (err) {
      console.error('Error saving order:', err);
      const errorMsg = err.response?.data?.message || 'Failed to save order';
      alert(errorMsg);
    }
  };

  const handleCancel = () => {
    navigate('/league-uniform-management');
  };

  // Step navigation
  const goToNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  // Validation for steps
  const canProceedFromStep1 = () => {
    return orderData.customer_name.trim() && orderData.contact_name.trim() && orderData.contact_phone.trim();
  };

  const canProceedFromStep2 = () => {
    // Must have at least one division with one team
    return divisions.length > 0 && divisions.some(d => d.teams.length > 0 && d.teams.some(t => t.team_name.trim()));
  };

  // Get all teams flat for product configuration
  const getAllTeams = () => {
    const teams = [];
    divisions.forEach(division => {
      division.teams.forEach(team => {
        teams.push({
          divisionId: division.id,
          divisionName: division.name,
          teamId: team.id,
          teamName: team.team_name,
          team: team
        });
      });
    });
    return teams;
  };

  // Check if a team has products configured
  const teamHasProducts = (team) => {
    // Check if any product data is filled
    const hasJerseys = team.jerseys.some(j => j.jersey_type || calculateYouthJerseyQty(j) > 0 || calculateAdultJerseyQty(j) > 0);
    const hasPants = team.pantData.type || calculateYouthPantQty(team.pantData) > 0 || calculateAdultPantQty(team.pantData) > 0;
    const hasBelts = team.beltData.youth_color || team.beltData.adult_color || parseInt(team.beltData.youth_quantity) > 0 || parseInt(team.beltData.adult_quantity) > 0;
    const hasSocks = team.sockData.sock_type || calculateSockTotal(team.sockData) > 0;
    const hasCaps = team.capData.cap_type || parseInt(team.capData.youth_quantity) > 0 || parseInt(team.capData.adult_quantity) > 0;
    return hasJerseys || hasPants || hasBelts || hasSocks || hasCaps;
  };

  // Render the wizard stepper
  const renderStepper = () => (
    <div className="wizard-stepper">
      {STEPS.map((step, index) => (
        <div
          key={step.number}
          className={`wizard-step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
          onClick={() => currentStep > step.number && goToStep(step.number)}
        >
          <div className="step-indicator">
            {currentStep > step.number ? (
              <span className="step-check">&#10003;</span>
            ) : (
              <span className="step-number">{step.icon}</span>
            )}
          </div>
          <span className="step-label">{step.label}</span>
          {index < STEPS.length - 1 && <div className="step-connector" />}
        </div>
      ))}
    </div>
  );

  // Render Step 1: League Info
  const renderStep1 = () => (
    <div className="wizard-step-content">
      <div className="step-header">
        <h2>League Information</h2>
        <p>Enter the basic information about the league and contact details.</p>
      </div>

      {isFromIntake && (
        <div className="intake-notice">
          <div className="intake-notice-icon">📋</div>
          <div className="intake-notice-content">
            <strong>Imported from League Portal</strong>
            <p>Submission #{intakeData.submissionNumber} — This order has been pre-filled with data from the intake submission. Review and adjust as needed.</p>
          </div>
        </div>
      )}

      <div className="form-section">
        <div className="form-grid">
          <div className="form-group league-autocomplete">
            <label htmlFor="customer_name">
              League Name <span className="required">*</span>
            </label>
            <div className="autocomplete-wrapper">
              <input
                type="text"
                id="customer_name"
                name="customer_name"
                value={orderData.customer_name}
                onChange={handleLeagueNameChange}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onFocus={() => orderData.customer_name.length >= 2 && searchLeagues(orderData.customer_name)}
                required
                readOnly={!!customer}
                className={customer ? 'read-only' : ''}
                placeholder="Search or enter league name"
                autoComplete="off"
              />
              {searchingLeagues && (
                <span className="search-indicator">Searching...</span>
              )}
              {showSuggestions && leagueSuggestions.length > 0 && (
                <ul className="suggestions-dropdown">
                  {leagueSuggestions.map((league) => (
                    <li
                      key={league.id}
                      onClick={() => handleLeagueSelect(league)}
                      className="suggestion-item"
                    >
                      <span className="league-name">{league.cust_name}</span>
                      {league.contact && (
                        <span className="league-contact">Contact: {league.contact}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contact_name">
              Contact Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="contact_name"
              name="contact_name"
              value={orderData.contact_name}
              onChange={handleInputChange}
              required
              placeholder="Enter contact name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contact_phone">
              Phone Number <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={orderData.contact_phone}
              onChange={handleInputChange}
              required
              placeholder="Enter phone number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="order_date">
              Order Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="order_date"
              name="order_date"
              value={orderData.order_date}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="due_date">Due Date</label>
            <input
              type="date"
              id="due_date"
              name="due_date"
              value={orderData.due_date}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">
              Status <span className="required">*</span>
            </label>
            <select
              id="status"
              name="status"
              value={orderData.status}
              onChange={handleInputChange}
              required
            >
              <option value="Pending">Pending</option>
              <option value="In Production">In Production</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="form-group full-width">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={orderData.notes}
              onChange={handleInputChange}
              placeholder="Add any notes about this order..."
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render Step 2: Structure (Divisions & Teams names only)
  const renderStep2 = () => (
    <div className="wizard-step-content">
      <div className="step-header">
        <h2>League Structure</h2>
        <p>Set up your divisions and teams. You'll configure products in the next step.</p>
      </div>

      <div className="structure-section">
        <div className="structure-header">
          <div className="structure-stats">
            {divisions.length > 0 && (
              <>
                <span className="stat-badge">{divisions.length} Division{divisions.length !== 1 ? 's' : ''}</span>
                <span className="stat-badge">{divisions.reduce((acc, d) => acc + d.teams.length, 0)} Teams</span>
              </>
            )}
          </div>
          <button type="button" onClick={addDivision} className="btn-add-division">
            + Add Division
          </button>
        </div>

        {divisions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">+</div>
            <h3>No Divisions Added</h3>
            <p>Start by adding a division (e.g., U8, U10, Adult League)</p>
            <button type="button" onClick={addDivision} className="btn-add-first">
              Add Your First Division
            </button>
          </div>
        ) : (
          <div className="divisions-structure-list">
            {divisions.map((division, divIndex) => (
              <div key={division.id} className="division-structure-card">
                <div className="division-structure-header">
                  <div className="division-info">
                    <span className="division-badge">Division {divIndex + 1}</span>
                    <input
                      type="text"
                      className="division-name-input-inline"
                      value={division.name}
                      onChange={(e) => updateDivisionName(division.id, e.target.value)}
                      placeholder="Enter division name (e.g., U8, U10)"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDivision(division.id)}
                    className="btn-remove-small"
                  >
                    Remove
                  </button>
                </div>

                <div className="teams-structure-section">
                  <div className="teams-structure-header">
                    <span className="teams-label">Teams ({division.teams.length})</span>
                    <button
                      type="button"
                      onClick={() => addTeam(division.id)}
                      className="btn-add-team-small"
                    >
                      + Add Team
                    </button>
                  </div>

                  {division.teams.length === 0 ? (
                    <div className="empty-teams-inline">
                      <p>No teams yet</p>
                      <button type="button" onClick={() => addTeam(division.id)} className="btn-link">
                        + Add first team
                      </button>
                    </div>
                  ) : (
                    <div className="teams-structure-grid">
                      {division.teams.map((team, teamIndex) => (
                        <div key={team.id} className="team-structure-item">
                          <span className="team-number">{teamIndex + 1}</span>
                          <input
                            type="text"
                            className="team-name-input"
                            value={team.team_name}
                            onChange={(e) => updateTeamField(division.id, team.id, 'team_name', e.target.value)}
                            placeholder="Team name"
                          />
                          <input
                            type="text"
                            className="sponsor-name-input"
                            value={team.sponsor_name}
                            onChange={(e) => updateTeamField(division.id, team.id, 'sponsor_name', e.target.value)}
                            placeholder="Sponsor (optional)"
                          />
                          <button
                            type="button"
                            onClick={() => removeTeam(division.id, team.id)}
                            className="btn-remove-team-small"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render product tabs content for a team
  const renderProductTabs = (division, team) => {
    const divisionId = division.id;
    const teamId = team.id;

    return (
      <div className="product-tabs-section team-products">
        <div className="tabs-container">
          <button
            type="button"
            className={`tab-button ${team.activeProductTab === 'jerseys' ? 'active' : ''}`}
            onClick={() => setActiveProductTab(divisionId, teamId, 'jerseys')}
          >
            Jerseys
          </button>
          <button
            type="button"
            className={`tab-button ${team.activeProductTab === 'pants' ? 'active' : ''}`}
            onClick={() => setActiveProductTab(divisionId, teamId, 'pants')}
          >
            Pants
          </button>
          <button
            type="button"
            className={`tab-button ${team.activeProductTab === 'belts' ? 'active' : ''}`}
            onClick={() => setActiveProductTab(divisionId, teamId, 'belts')}
          >
            Belts
          </button>
          <button
            type="button"
            className={`tab-button ${team.activeProductTab === 'socks' ? 'active' : ''}`}
            onClick={() => setActiveProductTab(divisionId, teamId, 'socks')}
          >
            Socks
          </button>
          <button
            type="button"
            className={`tab-button ${team.activeProductTab === 'caps' ? 'active' : ''}`}
            onClick={() => setActiveProductTab(divisionId, teamId, 'caps')}
          >
            Caps
          </button>
        </div>

        <div className="tab-content">
          {team.activeProductTab === 'jerseys' && renderJerseysTab(division, team)}
          {team.activeProductTab === 'pants' && renderPantsTab(division, team)}
          {team.activeProductTab === 'belts' && renderBeltsTab(division, team)}
          {team.activeProductTab === 'socks' && renderSocksTab(division, team)}
          {team.activeProductTab === 'caps' && renderCapsTab(division, team)}
        </div>
      </div>
    );
  };

  // Render jerseys tab
  const renderJerseysTab = (division, team) => {
    const divisionId = division.id;
    const teamId = team.id;

    return (
      <div className="product-tab-panel">
        <h3>Jerseys</h3>
        {team.jerseys.map((jersey, index) => (
          <div key={jersey.id} className="jersey-section">
            <div className="jersey-header">
              <h4>Jersey #{index + 1}</h4>
              <div className="jersey-header-actions">
                <button
                  type="button"
                  onClick={() => toggleJerseyCollapse(divisionId, teamId, jersey.id)}
                  className="btn-collapse"
                >
                  {jersey.collapsed ? '+ Expand' : '- Collapse'}
                </button>
                {team.jerseys.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeJersey(divisionId, teamId, jersey.id)}
                    className="btn-remove-jersey"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            {!jersey.collapsed && (
              <div className="jersey-content">
                <div className="form-grid" style={{ marginBottom: '30px' }}>
                  <div className="form-group">
                    <label>Jersey Type</label>
                    <input
                      type="text"
                      name="jersey_type"
                      value={jersey.jersey_type}
                      onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                      placeholder="Enter jersey type"
                    />
                  </div>
                  <div className="form-group">
                    <label>Fabric Type</label>
                    <input
                      type="text"
                      name="fabric_type"
                      value={jersey.fabric_type}
                      onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                      placeholder="Enter fabric type"
                    />
                  </div>
                  <div className="form-group">
                    <label>Color</label>
                    <input
                      type="text"
                      name="color"
                      value={jersey.color}
                      onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                      placeholder="Enter color"
                    />
                  </div>
                  <div className="form-group">
                    <label>Trim Type</label>
                    <input
                      type="text"
                      name="trim_type"
                      value={jersey.trim_type}
                      onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                      placeholder="Enter trim type"
                    />
                  </div>
                  <div className="form-group">
                    <label>Trim Color</label>
                    <input
                      type="text"
                      name="trim_color"
                      value={jersey.trim_color}
                      onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                      placeholder="Enter trim color"
                    />
                  </div>
                </div>

                {/* Print Types */}
                <div className="print-types-section" style={{ marginBottom: '20px' }}>
                  <h5 style={{ marginBottom: '10px' }}>Print Types</h5>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Front Print Type</label>
                      <input
                        type="text"
                        name="print_type_front"
                        value={jersey.print_type_front || ''}
                        onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                        placeholder="e.g., Screen Print, Heat Press, Sublimation"
                      />
                    </div>
                    <div className="form-group">
                      <label>Sponsor Print Type</label>
                      <input
                        type="text"
                        name="print_type_sponsor"
                        value={jersey.print_type_sponsor || ''}
                        onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                        placeholder="e.g., Screen Print, Heat Press, Embroidery"
                      />
                    </div>
                    <div className="form-group">
                      <label>Numbers Print Type</label>
                      <input
                        type="text"
                        name="print_type_numbers"
                        value={jersey.print_type_numbers || ''}
                        onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                        placeholder="e.g., Heat Press, Tackle Twill, Screen Print"
                      />
                    </div>
                  </div>
                </div>

                {/* Print Colors */}
                <div className="print-colors-section">
                  <div className="print-colors-header">
                    <h5>Print Colors</h5>
                    <div className="print-colors-controls">
                      <div className="print-colors-count">
                        <label>Number of Colors:</label>
                        <select
                          value={(jersey.print_colors || [{ name: '' }]).length}
                          onChange={(e) => handlePrintColorCountChange(divisionId, teamId, jersey.id, parseInt(e.target.value))}
                          disabled={jersey.print_colors_free}
                        >
                          <option value={1}>1 Color - $3.25</option>
                          <option value={2}>2 Colors - $4.50</option>
                        </select>
                      </div>
                      <label className={`placement-chip ${jersey.print_colors_free ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={jersey.print_colors_free || false}
                          onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, { target: { name: 'print_colors_free', value: e.target.checked, type: 'checkbox' } })}
                        />
                        <span>Free / No Charge</span>
                      </label>
                    </div>
                  </div>
                  <div className="print-colors-grid">
                    {(jersey.print_colors || [{ name: '' }]).map((color, index) => (
                      <div key={index} className="print-color-item">
                        <div className="print-color-number">Color {index + 1}</div>
                        <div className="print-color-inputs">
                          <input
                            type="text"
                            value={color.name}
                            onChange={(e) => handlePrintColorChange(divisionId, teamId, jersey.id, index, 'name', e.target.value)}
                            placeholder="Color name"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="print-colors-total">
                    Print Cost: ${calculatePrintColorCostPerJersey(jersey).toFixed(2)}/jersey
                  </div>
                </div>

                {/* Print Colors by Location */}
                <div className="print-location-colors-section">
                  <h5>Print Colors by Location</h5>
                  <div className="print-location-grid">
                    <div className="form-group">
                      <label>Team Print Color</label>
                      <input
                        type="text"
                        name="team_print_color"
                        value={jersey.team_print_color}
                        onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                        placeholder="e.g., Orange"
                      />
                    </div>
                    <div className="form-group">
                      <label>Sponsor Color</label>
                      <input
                        type="text"
                        name="sponsor_color"
                        value={jersey.sponsor_color}
                        onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                        placeholder="e.g., Orange"
                      />
                    </div>
                    <div className="form-group">
                      <label>Numbers Color</label>
                      <input
                        type="text"
                        name="numbers_color"
                        value={jersey.numbers_color}
                        onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                        placeholder="e.g., Orange"
                      />
                    </div>
                  </div>
                  <div className="numbers-placement">
                    <span className="placement-label">Number Placement:</span>
                    <label className={`placement-chip ${jersey.numbers_front ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={jersey.numbers_front}
                        onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, { target: { name: 'numbers_front', value: e.target.checked, type: 'checkbox' } })}
                      />
                      <span>Front</span>
                    </label>
                    <label className={`placement-chip ${jersey.numbers_back ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={jersey.numbers_back}
                        onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, { target: { name: 'numbers_back', value: e.target.checked, type: 'checkbox' } })}
                      />
                      <span>Back</span>
                    </label>
                  </div>
                </div>

                {/* Youth Sizes */}
                <div className="size-grid-section">
                  <div className="size-section-header">
                    <h4>Youth Sizes</h4>
                    <div className="sponsor-inline">
                      <span className="sponsor-inline-label">Sponsor:</span>
                      <label className={`sponsor-chip ${jersey.youth_sponsor_front ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={jersey.youth_sponsor_front}
                          onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'youth_sponsor_front', e.target.checked)}
                        />
                        <span>Front +$3.50</span>
                      </label>
                      <label className={`sponsor-chip ${jersey.youth_sponsor_back ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={jersey.youth_sponsor_back}
                          onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'youth_sponsor_back', e.target.checked)}
                        />
                        <span>Back +$3.50</span>
                      </label>
                    </div>
                  </div>
                  <div className="size-grid">
                    {['YXXS', 'YXS', 'YS', 'YM', 'YL', 'YXL'].map(size => (
                      <div key={size} className="size-grid-item">
                        <label>{size}</label>
                        <input
                          type="number"
                          value={jersey.youth_sizes[size]}
                          onChange={(e) => handleJerseySizeChange(divisionId, teamId, jersey.id, 'youth_sizes', size, e.target.value)}
                          placeholder="Qty"
                          min="0"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="size-metadata">
                    <div className="form-group">
                      <label>Brand</label>
                      <input type="text" name="youth_brand" value={jersey.youth_brand} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter brand" />
                    </div>
                    <div className="form-group">
                      <label>Vendor #</label>
                      <input type="text" name="youth_vendor_number" value={jersey.youth_vendor_number} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter vendor number" />
                    </div>
                    <div className="form-group">
                      <label>SKU</label>
                      <input type="text" name="youth_sku" value={jersey.youth_sku} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter SKU" />
                    </div>
                    <div className="form-group">
                      <label>Quantity Price</label>
                      <input type="number" name="youth_quantity_price" value={jersey.youth_quantity_price} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter price per item" min="0" step="0.01" />
                    </div>
                  </div>

                  {/* Youth Numbers Section */}
                  <div className="numbers-section">
                    <div className="numbers-header">
                      <h5>Numbers</h5>
                      <div className="numbers-toggles">
                        <label className={`numbers-toggle ${jersey.youth_numbers_consecutive ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={jersey.youth_numbers_consecutive || false}
                            onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'youth_numbers_consecutive', e.target.checked)}
                          />
                          <span>Consecutive</span>
                        </label>
                        <label className={`numbers-toggle ${jersey.youth_numbers_special ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={jersey.youth_numbers_special || false}
                            onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'youth_numbers_special', e.target.checked)}
                          />
                          <span>Special</span>
                        </label>
                      </div>
                    </div>
                    {(jersey.youth_numbers_consecutive || jersey.youth_numbers_special) && (
                      <div className="numbers-options">
                        {jersey.youth_numbers_consecutive && (
                          <div className="numbers-option">
                            <div className="numbers-option-header">Consecutive Numbers</div>
                            <div className="numbers-option-inputs">
                              <div className="form-group">
                                <label>Start At</label>
                                <input
                                  type="number"
                                  name="youth_consecutive_start"
                                  value={jersey.youth_consecutive_start}
                                  onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                                  placeholder="e.g., 1"
                                  min="0"
                                />
                              </div>
                              <div className="form-group">
                                <label>Cost/Jersey</label>
                                <input
                                  type="number"
                                  name="youth_consecutive_cost"
                                  value={jersey.youth_consecutive_cost}
                                  onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                                  placeholder="$0.00"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {jersey.youth_numbers_special && (
                          <div className="numbers-option">
                            <div className="numbers-option-header">
                              Special Numbers
                              <span className="locked-price">$4.50/jersey</span>
                            </div>
                            <div className="special-numbers-grid">
                              {['YXXS', 'YXS', 'YS', 'YM', 'YL', 'YXL'].map(size => {
                                const qty = parseInt(jersey.youth_sizes[size]) || 0;
                                if (qty === 0) return null;
                                return (
                                  <div key={size} className="special-number-item">
                                    <label>{size} ({qty})</label>
                                    <input
                                      type="text"
                                      value={(jersey.youth_special_numbers || {})[size] || ''}
                                      onChange={(e) => handleJerseySizeChange(divisionId, teamId, jersey.id, 'youth_special_numbers', size, e.target.value)}
                                      placeholder="e.g., 7, 23, 45"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {(jersey.youth_numbers_consecutive || jersey.youth_numbers_special) && (
                      <div className="numbers-summary">
                        {(() => {
                          const numCost = calculateYouthNumberingCost(jersey);
                          return (
                            <>
                              {numCost.consecutiveCount > 0 && (
                                <span className="numbers-summary-item">
                                  Consecutive: {numCost.consecutiveCount} × ${jersey.youth_consecutive_cost || '0'} = ${numCost.consecutiveTotal.toFixed(2)}
                                </span>
                              )}
                              {numCost.specialCount > 0 && (
                                <span className="numbers-summary-item">
                                  Special: {numCost.specialCount} × $4.50 = ${numCost.specialTotal.toFixed(2)}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="pant-total">
                    <span className="total-label">Youth Total{(jersey.youth_sponsor_front || jersey.youth_sponsor_back) ? ` (incl. ${getSponsorPlacementText(jersey.youth_sponsor_front, jersey.youth_sponsor_back)} sponsor)` : ''}:</span>
                    <span className="total-value">{calculateYouthJerseyQty(jersey)} items - ${calculateYouthJerseyTotal(jersey)}</span>
                  </div>
                </div>

                {/* Adult Sizes */}
                <div className="size-grid-section">
                  <div className="size-section-header">
                    <h4>Adult Sizes</h4>
                    <div className="sponsor-inline">
                      <span className="sponsor-inline-label">Sponsor:</span>
                      <label className={`sponsor-chip ${jersey.adult_sponsor_front ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={jersey.adult_sponsor_front}
                          onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'adult_sponsor_front', e.target.checked)}
                        />
                        <span>Front +$3.50</span>
                      </label>
                      <label className={`sponsor-chip ${jersey.adult_sponsor_back ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={jersey.adult_sponsor_back}
                          onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'adult_sponsor_back', e.target.checked)}
                        />
                        <span>Back +$3.50</span>
                      </label>
                    </div>
                  </div>
                  <div className="size-grid">
                    {['AXS', 'AS', 'AM', 'AL', 'AXL', 'A2X', 'A3X'].map(size => (
                      <div key={size} className="size-grid-item">
                        <label>{size}</label>
                        <input
                          type="number"
                          value={jersey.adult_sizes[size]}
                          onChange={(e) => handleJerseySizeChange(divisionId, teamId, jersey.id, 'adult_sizes', size, e.target.value)}
                          placeholder="Qty"
                          min="0"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="size-metadata">
                    <div className="form-group">
                      <label>Brand</label>
                      <input type="text" name="adult_brand" value={jersey.adult_brand} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter brand" />
                    </div>
                    <div className="form-group">
                      <label>Vendor #</label>
                      <input type="text" name="adult_vendor_number" value={jersey.adult_vendor_number} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter vendor number" />
                    </div>
                    <div className="form-group">
                      <label>SKU</label>
                      <input type="text" name="adult_sku" value={jersey.adult_sku} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter SKU" />
                    </div>
                    <div className="form-group">
                      <label>Quantity Price</label>
                      <input type="number" name="adult_quantity_price" value={jersey.adult_quantity_price} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter price per item" min="0" step="0.01" />
                    </div>
                  </div>

                  {/* Adult Numbers Section */}
                  <div className="numbers-section">
                    <div className="numbers-header">
                      <h5>Numbers</h5>
                      <div className="numbers-toggles">
                        <label className={`numbers-toggle ${jersey.adult_numbers_consecutive ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={jersey.adult_numbers_consecutive || false}
                            onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'adult_numbers_consecutive', e.target.checked)}
                          />
                          <span>Consecutive</span>
                        </label>
                        <label className={`numbers-toggle ${jersey.adult_numbers_special ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={jersey.adult_numbers_special || false}
                            onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'adult_numbers_special', e.target.checked)}
                          />
                          <span>Special</span>
                        </label>
                      </div>
                    </div>
                    {(jersey.adult_numbers_consecutive || jersey.adult_numbers_special) && (
                      <div className="numbers-options">
                        {jersey.adult_numbers_consecutive && (
                          <div className="numbers-option">
                            <div className="numbers-option-header">Consecutive Numbers</div>
                            <div className="numbers-option-inputs">
                              <div className="form-group">
                                <label>Start At</label>
                                <input
                                  type="number"
                                  name="adult_consecutive_start"
                                  value={jersey.adult_consecutive_start}
                                  onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                                  placeholder="e.g., 1"
                                  min="0"
                                />
                              </div>
                              <div className="form-group">
                                <label>Cost/Jersey</label>
                                <input
                                  type="number"
                                  name="adult_consecutive_cost"
                                  value={jersey.adult_consecutive_cost}
                                  onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                                  placeholder="$0.00"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {jersey.adult_numbers_special && (
                          <div className="numbers-option">
                            <div className="numbers-option-header">
                              Special Numbers
                              <span className="locked-price">$4.50/jersey</span>
                            </div>
                            <div className="special-numbers-grid">
                              {['AXS', 'AS', 'AM', 'AL', 'AXL', 'A2X', 'A3X'].map(size => {
                                const qty = parseInt(jersey.adult_sizes[size]) || 0;
                                if (qty === 0) return null;
                                return (
                                  <div key={size} className="special-number-item">
                                    <label>{size} ({qty})</label>
                                    <input
                                      type="text"
                                      value={(jersey.adult_special_numbers || {})[size] || ''}
                                      onChange={(e) => handleJerseySizeChange(divisionId, teamId, jersey.id, 'adult_special_numbers', size, e.target.value)}
                                      placeholder="e.g., 7, 23, 45"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {(jersey.adult_numbers_consecutive || jersey.adult_numbers_special) && (
                      <div className="numbers-summary">
                        {(() => {
                          const numCost = calculateAdultNumberingCost(jersey);
                          return (
                            <>
                              {numCost.consecutiveCount > 0 && (
                                <span className="numbers-summary-item">
                                  Consecutive: {numCost.consecutiveCount} × ${jersey.adult_consecutive_cost || '0'} = ${numCost.consecutiveTotal.toFixed(2)}
                                </span>
                              )}
                              {numCost.specialCount > 0 && (
                                <span className="numbers-summary-item">
                                  Special: {numCost.specialCount} × $4.50 = ${numCost.specialTotal.toFixed(2)}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="pant-total">
                    <span className="total-label">Adult Total{(jersey.adult_sponsor_front || jersey.adult_sponsor_back) ? ` (incl. ${getSponsorPlacementText(jersey.adult_sponsor_front, jersey.adult_sponsor_back)} sponsor)` : ''}:</span>
                    <span className="total-value">{calculateAdultJerseyQty(jersey)} items - ${calculateAdultJerseyTotal(jersey)}</span>
                  </div>
                </div>

                {/* Coach Sizes */}
                <div className="size-grid-section">
                  <div className="size-section-header">
                    <h4>Coach Sizes</h4>
                    <div className="sponsor-inline">
                      <span className="sponsor-inline-label">Sponsor:</span>
                      <label className={`sponsor-chip ${jersey.coach_sponsor_front ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={jersey.coach_sponsor_front}
                          onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'coach_sponsor_front', e.target.checked)}
                        />
                        <span>Front +$3.50</span>
                      </label>
                      <label className={`sponsor-chip ${jersey.coach_sponsor_back ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={jersey.coach_sponsor_back}
                          onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'coach_sponsor_back', e.target.checked)}
                        />
                        <span>Back +$3.50</span>
                      </label>
                    </div>
                  </div>
                  <div className="size-grid">
                    {['S', 'M', 'L', 'XL', '2X', '3X'].map(size => (
                      <div key={size} className="size-grid-item">
                        <label>{size}</label>
                        <input
                          type="number"
                          value={jersey.coach_sizes[size]}
                          onChange={(e) => handleJerseySizeChange(divisionId, teamId, jersey.id, 'coach_sizes', size, e.target.value)}
                          placeholder="Qty"
                          min="0"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="size-metadata">
                    <div className="form-group">
                      <label>Brand</label>
                      <input type="text" name="coach_brand" value={jersey.coach_brand} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter brand" />
                    </div>
                    <div className="form-group">
                      <label>Vendor #</label>
                      <input type="text" name="coach_vendor_number" value={jersey.coach_vendor_number} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter vendor number" />
                    </div>
                    <div className="form-group">
                      <label>SKU</label>
                      <input type="text" name="coach_sku" value={jersey.coach_sku} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter SKU" />
                    </div>
                    <div className="form-group">
                      <label>Quantity Price</label>
                      <input type="number" name="coach_quantity_price" value={jersey.coach_quantity_price} onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)} placeholder="Enter price per item" min="0" step="0.01" />
                    </div>
                  </div>

                  {/* Coach Numbers Section */}
                  <div className="numbers-section">
                    <div className="numbers-header">
                      <h5>Numbers</h5>
                      <div className="numbers-toggles">
                        <label className={`numbers-toggle ${jersey.coach_numbers_consecutive ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={jersey.coach_numbers_consecutive || false}
                            onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'coach_numbers_consecutive', e.target.checked)}
                          />
                          <span>Consecutive</span>
                        </label>
                        <label className={`numbers-toggle ${jersey.coach_numbers_special ? 'active' : ''}`}>
                          <input
                            type="checkbox"
                            checked={jersey.coach_numbers_special || false}
                            onChange={(e) => handleJerseySponsorChange(divisionId, teamId, jersey.id, 'coach_numbers_special', e.target.checked)}
                          />
                          <span>Special</span>
                        </label>
                      </div>
                    </div>
                    {(jersey.coach_numbers_consecutive || jersey.coach_numbers_special) && (
                      <div className="numbers-options">
                        {jersey.coach_numbers_consecutive && (
                          <div className="numbers-option">
                            <div className="numbers-option-header">Consecutive Numbers</div>
                            <div className="numbers-option-inputs">
                              <div className="form-group">
                                <label>Start At</label>
                                <input
                                  type="number"
                                  name="coach_consecutive_start"
                                  value={jersey.coach_consecutive_start}
                                  onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                                  placeholder="e.g., 1"
                                  min="0"
                                />
                              </div>
                              <div className="form-group">
                                <label>Cost/Jersey</label>
                                <input
                                  type="number"
                                  name="coach_consecutive_cost"
                                  value={jersey.coach_consecutive_cost}
                                  onChange={(e) => handleJerseyBasicChange(divisionId, teamId, jersey.id, e)}
                                  placeholder="$0.00"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {jersey.coach_numbers_special && (
                          <div className="numbers-option">
                            <div className="numbers-option-header">
                              Special Numbers
                              <span className="locked-price">$4.50/jersey</span>
                            </div>
                            <div className="special-numbers-grid">
                              {['S', 'M', 'L', 'XL', '2X', '3X'].map(size => {
                                const qty = parseInt(jersey.coach_sizes[size]) || 0;
                                if (qty === 0) return null;
                                return (
                                  <div key={size} className="special-number-item">
                                    <label>{size} ({qty})</label>
                                    <input
                                      type="text"
                                      value={(jersey.coach_special_numbers || {})[size] || ''}
                                      onChange={(e) => handleJerseySizeChange(divisionId, teamId, jersey.id, 'coach_special_numbers', size, e.target.value)}
                                      placeholder="e.g., 7, 23, 45"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {(jersey.coach_numbers_consecutive || jersey.coach_numbers_special) && (
                      <div className="numbers-summary">
                        {(() => {
                          const numCost = calculateCoachNumberingCost(jersey);
                          return (
                            <>
                              {numCost.consecutiveCount > 0 && (
                                <span className="numbers-summary-item">
                                  Consecutive: {numCost.consecutiveCount} × ${jersey.coach_consecutive_cost || '0'} = ${numCost.consecutiveTotal.toFixed(2)}
                                </span>
                              )}
                              {numCost.specialCount > 0 && (
                                <span className="numbers-summary-item">
                                  Special: {numCost.specialCount} × $4.50 = ${numCost.specialTotal.toFixed(2)}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="pant-total">
                    <span className="total-label">Coach Total{(jersey.coach_sponsor_front || jersey.coach_sponsor_back) ? ` (incl. ${getSponsorPlacementText(jersey.coach_sponsor_front, jersey.coach_sponsor_back)} sponsor)` : ''}:</span>
                    <span className="total-value">{calculateCoachJerseyQty(jersey)} items - ${calculateCoachJerseyTotal(jersey)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        <button type="button" onClick={() => addJersey(divisionId, teamId)} className="btn-add-jersey">
          + Add Another Jersey
        </button>
      </div>
    );
  };

  // Render pants tab
  const renderPantsTab = (division, team) => {
    const divisionId = division.id;
    const teamId = team.id;
    const pantData = team.pantData;

    return (
      <div className="product-tab-panel">
        <h3>Pants</h3>
        <div className="form-grid" style={{ marginBottom: '30px' }}>
          <div className="form-group">
            <label>Type</label>
            <input type="text" name="type" value={pantData.type} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter pant type" />
          </div>
          <div className="form-group">
            <label>Fabric</label>
            <input type="text" name="fabric" value={pantData.fabric} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter fabric" />
          </div>
          <div className="form-group">
            <label>Color</label>
            <input type="text" name="color" value={pantData.color} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter color" />
          </div>
          <div className="form-group">
            <label>Trim Type</label>
            <input type="text" name="trim_type" value={pantData.trim_type} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter trim type" />
          </div>
          <div className="form-group">
            <label>Trim Color</label>
            <input type="text" name="trim_color" value={pantData.trim_color} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter trim color" />
          </div>
          <div className="form-group">
            <label>Brand</label>
            <input type="text" name="brand" value={pantData.brand} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter brand" />
          </div>
        </div>

        {/* Youth Sizes */}
        <div className="size-grid-section">
          <h4>Youth Sizes</h4>
          <div className="size-grid">
            {['YXS', 'YS', 'YM', 'YL', 'YXL'].map(size => (
              <div key={size} className="size-grid-item">
                <label>{size}</label>
                <input type="number" value={pantData.youth_sizes[size]} onChange={(e) => handlePantSizeChange(divisionId, teamId, 'youth_sizes', size, e.target.value)} placeholder="Qty" min="0" />
              </div>
            ))}
          </div>
          <div className="size-metadata">
            <div className="form-group">
              <label>Youth Vendor #</label>
              <input type="text" name="youth_vendor_number" value={pantData.youth_vendor_number} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter youth vendor number" />
            </div>
            <div className="form-group">
              <label>Youth SKU</label>
              <input type="text" name="youth_sku" value={pantData.youth_sku} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter youth SKU" />
            </div>
            <div className="form-group">
              <label>Quantity Price</label>
              <input type="number" name="youth_quantity_price" value={pantData.youth_quantity_price} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter price per item" min="0" step="0.01" />
            </div>
          </div>
          <div className="pant-total">
            <span className="total-label">Youth Total:</span>
            <span className="total-value">{calculateYouthPantQty(pantData)} items - ${calculateYouthPantTotal(pantData)}</span>
          </div>
        </div>

        {/* Adult Sizes */}
        <div className="size-grid-section">
          <h4>Adult Sizes</h4>
          <div className="size-grid">
            {['AXS', 'AS', 'AM', 'AL', 'AXL', 'A2X', 'A3X'].map(size => (
              <div key={size} className="size-grid-item">
                <label>{size}</label>
                <input type="number" value={pantData.adult_sizes[size]} onChange={(e) => handlePantSizeChange(divisionId, teamId, 'adult_sizes', size, e.target.value)} placeholder="Qty" min="0" />
              </div>
            ))}
          </div>
          <div className="size-metadata">
            <div className="form-group">
              <label>Adult Vendor #</label>
              <input type="text" name="adult_vendor_number" value={pantData.adult_vendor_number} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter adult vendor number" />
            </div>
            <div className="form-group">
              <label>Adult SKU</label>
              <input type="text" name="adult_sku" value={pantData.adult_sku} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter adult SKU" />
            </div>
            <div className="form-group">
              <label>Quantity Price</label>
              <input type="number" name="adult_quantity_price" value={pantData.adult_quantity_price} onChange={(e) => handlePantBasicChange(divisionId, teamId, e)} placeholder="Enter price per item" min="0" step="0.01" />
            </div>
          </div>
          <div className="pant-total">
            <span className="total-label">Adult Total:</span>
            <span className="total-value">{calculateAdultPantQty(pantData)} items - ${calculateAdultPantTotal(pantData)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Render belts tab
  const renderBeltsTab = (division, team) => {
    const divisionId = division.id;
    const teamId = team.id;
    const beltData = team.beltData;

    const calculateYouthBeltTotal = () => {
      const qty = parseInt(beltData.youth_quantity) || 0;
      const price = parseFloat(beltData.youth_quantity_price) || 0;
      return (qty * price).toFixed(2);
    };

    const calculateAdultBeltTotal = () => {
      const qty = parseInt(beltData.adult_quantity) || 0;
      const price = parseFloat(beltData.adult_quantity_price) || 0;
      return (qty * price).toFixed(2);
    };

    return (
      <div className="product-tab-panel">
        <h3>Belts</h3>

        {/* Youth Belts */}
        <div className="size-grid-section">
          <h4>Youth Belts</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Color</label>
              <input type="text" name="youth_color" value={beltData.youth_color || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter belt color" />
            </div>
            <div className="form-group">
              <label>Brand</label>
              <input type="text" name="youth_brand" value={beltData.youth_brand || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter brand" />
            </div>
            <div className="form-group">
              <label>Vendor #</label>
              <input type="text" name="youth_vendor_number" value={beltData.youth_vendor_number || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter vendor number" />
            </div>
            <div className="form-group">
              <label>SKU</label>
              <input type="text" name="youth_sku" value={beltData.youth_sku || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter SKU" />
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" name="youth_quantity" value={beltData.youth_quantity || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter quantity" min="0" />
            </div>
            <div className="form-group">
              <label>Price per Belt</label>
              <input type="number" name="youth_quantity_price" value={beltData.youth_quantity_price || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter price" min="0" step="0.01" />
            </div>
          </div>
          <div className="pant-total">
            <span className="total-label">Youth Belts Total:</span>
            <span className="total-value">{beltData.youth_quantity || 0} belts - ${calculateYouthBeltTotal()}</span>
          </div>
        </div>

        {/* Adult Belts */}
        <div className="size-grid-section">
          <h4>Adult Belts</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Color</label>
              <input type="text" name="adult_color" value={beltData.adult_color || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter belt color" />
            </div>
            <div className="form-group">
              <label>Brand</label>
              <input type="text" name="adult_brand" value={beltData.adult_brand || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter brand" />
            </div>
            <div className="form-group">
              <label>Vendor #</label>
              <input type="text" name="adult_vendor_number" value={beltData.adult_vendor_number || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter vendor number" />
            </div>
            <div className="form-group">
              <label>SKU</label>
              <input type="text" name="adult_sku" value={beltData.adult_sku || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter SKU" />
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" name="adult_quantity" value={beltData.adult_quantity || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter quantity" min="0" />
            </div>
            <div className="form-group">
              <label>Price per Belt</label>
              <input type="number" name="adult_quantity_price" value={beltData.adult_quantity_price || ''} onChange={(e) => handleBeltChange(divisionId, teamId, e)} placeholder="Enter price" min="0" step="0.01" />
            </div>
          </div>
          <div className="pant-total">
            <span className="total-label">Adult Belts Total:</span>
            <span className="total-value">{beltData.adult_quantity || 0} belts - ${calculateAdultBeltTotal()}</span>
          </div>
        </div>
      </div>
    );
  };

  // Render socks tab
  const renderSocksTab = (division, team) => {
    const divisionId = division.id;
    const teamId = team.id;
    const sockData = team.sockData;

    return (
      <div className="product-tab-panel">
        <h3>Socks</h3>

        {/* Basic sock info in a compact row */}
        <div className="sock-basic-info">
          <div className="sock-basic-field">
            <label>Sock Type</label>
            <input type="text" name="sock_type" value={sockData.sock_type} onChange={(e) => handleSockBasicChange(divisionId, teamId, e)} placeholder="e.g., Crew, Ankle" />
          </div>
          <div className="sock-basic-field">
            <label>Color</label>
            <input type="text" name="color" value={sockData.color} onChange={(e) => handleSockBasicChange(divisionId, teamId, e)} placeholder="e.g., Navy, Red" />
          </div>
          <div className="sock-basic-field">
            <label>Brand</label>
            <input type="text" name="brand" value={sockData.brand} onChange={(e) => handleSockBasicChange(divisionId, teamId, e)} placeholder="e.g., TCK, Nike" />
          </div>
        </div>

        {/* Compact table for all sizes */}
        <div className="sock-sizes-table-container">
          <table className="sock-sizes-table">
            <thead>
              <tr>
                <th className="size-col">Size</th>
                <th className="qty-col">Qty</th>
                <th className="vendor-col">Vendor #</th>
                <th className="sku-col">SKU</th>
                <th className="price-col">Unit Price</th>
              </tr>
            </thead>
            <tbody>
              {['XS', 'S', '6-8', '9-11', '10-13'].map(size => (
                <tr key={size}>
                  <td className="size-label">{SOCK_SIZE_LABELS[size]}</td>
                  <td>
                    <input
                      type="number"
                      value={sockData.sizes[size].quantity}
                      onChange={(e) => handleSockSizeChange(divisionId, teamId, size, 'quantity', e.target.value)}
                      placeholder="0"
                      min="0"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={sockData.sizes[size].vendor_number}
                      onChange={(e) => handleSockSizeChange(divisionId, teamId, size, 'vendor_number', e.target.value)}
                      placeholder="Vendor #"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={sockData.sizes[size].sku}
                      onChange={(e) => handleSockSizeChange(divisionId, teamId, size, 'sku', e.target.value)}
                      placeholder="SKU"
                    />
                  </td>
                  <td>
                    <div className="price-input-wrapper">
                      <span className="currency-symbol">$</span>
                      <input
                        type="number"
                        value={sockData.sizes[size].quantity_price}
                        onChange={(e) => handleSockSizeChange(divisionId, teamId, size, 'quantity_price', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sock-total">
          <span className="total-label">Total Socks:</span>
          <span className="total-value">{calculateSockTotal(sockData)}</span>
        </div>
      </div>
    );
  };

  // Render caps tab
  const renderCapsTab = (division, team) => {
    const divisionId = division.id;
    const teamId = team.id;
    const capData = team.capData;

    return (
      <div className="product-tab-panel">
        <h3>Caps</h3>
        <div className="form-grid" style={{ marginBottom: '30px' }}>
          <div className="form-group">
            <label>Cap Type</label>
            <input type="text" name="cap_type" value={capData.cap_type} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter cap type" />
          </div>
          <div className="form-group">
            <label>Sizing</label>
            <input type="text" name="sizing" value={capData.sizing} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter sizing" />
          </div>
          <div className="form-group">
            <label>Fabric</label>
            <input type="text" name="fabric" value={capData.fabric} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter fabric" />
          </div>
          <div className="form-group">
            <label>Cap Color</label>
            <input type="text" name="cap_color" value={capData.cap_color} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter cap color" />
          </div>
          <div className="form-group">
            <label>Crown</label>
            <input type="text" name="crown" value={capData.crown} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter crown" />
          </div>
          <div className="form-group">
            <label>Visor</label>
            <input type="text" name="visor" value={capData.visor} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter visor" />
          </div>
          <div className="form-group">
            <label>Eye</label>
            <input type="text" name="eye" value={capData.eye} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter eye" />
          </div>
          <div className="form-group">
            <label>Button</label>
            <input type="text" name="button" value={capData.button} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter button" />
          </div>
          <div className="form-group">
            <label>Brand</label>
            <input type="text" name="brand" value={capData.brand} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter brand" />
          </div>
        </div>

        {/* Youth Section */}
        <div className="cap-metadata-section">
          <h4>Youth Information</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Youth Vendor #</label>
              <input type="text" name="youth_vendor_number" value={capData.youth_vendor_number} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter youth vendor number" />
            </div>
            <div className="form-group">
              <label>Youth SKU</label>
              <input type="text" name="youth_sku" value={capData.youth_sku} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter youth SKU" />
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" name="youth_quantity" value={capData.youth_quantity} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter quantity" min="0" />
            </div>
            <div className="form-group">
              <label>Quantity Price</label>
              <input type="number" name="youth_quantity_price" value={capData.youth_quantity_price} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter price per item" min="0" step="0.01" />
            </div>
          </div>
          <div className="pant-total">
            <span className="total-label">Youth Total:</span>
            <span className="total-value">{capData.youth_quantity || 0} caps - ${calculateYouthCapTotal(capData)}</span>
          </div>
        </div>

        {/* Adult Section */}
        <div className="cap-metadata-section">
          <h4>Adult Information</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Adult Vendor #</label>
              <input type="text" name="adult_vendor_number" value={capData.adult_vendor_number} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter adult vendor number" />
            </div>
            <div className="form-group">
              <label>Adult SKU</label>
              <input type="text" name="adult_sku" value={capData.adult_sku} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter adult SKU" />
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" name="adult_quantity" value={capData.adult_quantity} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter quantity" min="0" />
            </div>
            <div className="form-group">
              <label>Quantity Price</label>
              <input type="number" name="adult_quantity_price" value={capData.adult_quantity_price} onChange={(e) => handleCapBasicChange(divisionId, teamId, e)} placeholder="Enter price per item" min="0" step="0.01" />
            </div>
          </div>
          <div className="pant-total">
            <span className="total-label">Adult Total:</span>
            <span className="total-value">{capData.adult_quantity || 0} caps - ${calculateAdultCapTotal(capData)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Render Step 3: Products Configuration
  const renderStep3 = () => {
    const allTeams = getAllTeams();

    return (
      <div className="wizard-step-content">
        <div className="step-header">
          <h2>Configure Products</h2>
          <p>Select a team to configure their uniform products.</p>
        </div>

        <div className="products-step-layout">
          {/* Team selector sidebar */}
          <div className="team-selector-sidebar">
            <h3>Teams</h3>
            {allTeams.length === 0 ? (
              <p className="no-teams-msg">No teams added yet. Go back to add teams.</p>
            ) : (
              <ul className="team-selector-list">
                {allTeams.map((teamInfo, idx) => (
                  <li
                    key={`${teamInfo.divisionId}-${teamInfo.teamId}`}
                    className={`team-selector-item ${activeTeamConfig?.divisionId === teamInfo.divisionId && activeTeamConfig?.teamId === teamInfo.teamId ? 'active' : ''} ${teamHasProducts(teamInfo.team) ? 'has-products' : ''}`}
                    onClick={() => setActiveTeamConfig(teamInfo)}
                  >
                    <div className="team-selector-info">
                      <span className="team-selector-name">{teamInfo.teamName || `Team ${idx + 1}`}</span>
                      <span className="team-selector-division">{teamInfo.divisionName || 'Division'}</span>
                    </div>
                    {teamHasProducts(teamInfo.team) && (
                      <span className="configured-badge">Configured</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Product configuration area */}
          <div className="product-config-area">
            {!activeTeamConfig ? (
              <div className="select-team-prompt">
                <div className="prompt-icon">&#8592;</div>
                <h3>Select a Team</h3>
                <p>Choose a team from the list to configure their products.</p>
              </div>
            ) : (
              <div className="team-product-config">
                <div className="team-config-header">
                  <h3>{activeTeamConfig.teamName || 'Unnamed Team'}</h3>
                  <span className="division-label">{activeTeamConfig.divisionName}</span>
                </div>
                {divisions.map(division =>
                  division.id === activeTeamConfig.divisionId &&
                  division.teams.map(team =>
                    team.id === activeTeamConfig.teamId &&
                    renderProductTabs(division, team)
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Step 4: Review
  const renderStep4 = () => {
    const totals = calculateOrderTotals();

    return (
      <div className="wizard-step-content">
        <div className="step-header">
          <h2>Review Order</h2>
          <p>Review your order details before submitting.</p>
        </div>

        <div className="review-section">
          {/* League Info Summary */}
          <div className="review-card">
            <div className="review-card-header">
              <h3>League Information</h3>
              <button type="button" className="btn-edit-section" onClick={() => goToStep(1)}>Edit</button>
            </div>
            <div className="review-card-body">
              <div className="review-grid">
                <div className="review-item">
                  <span className="review-label">League Name</span>
                  <span className="review-value">{orderData.customer_name || '-'}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Contact</span>
                  <span className="review-value">{orderData.contact_name || '-'}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Phone</span>
                  <span className="review-value">{orderData.contact_phone || '-'}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Order Date</span>
                  <span className="review-value">{orderData.order_date || '-'}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Due Date</span>
                  <span className="review-value">{orderData.due_date || '-'}</span>
                </div>
                <div className="review-item">
                  <span className="review-label">Status</span>
                  <span className="review-value">{orderData.status}</span>
                </div>
              </div>
              {orderData.notes && (
                <div className="review-notes">
                  <span className="review-label">Notes</span>
                  <p>{orderData.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Structure Summary */}
          <div className="review-card">
            <div className="review-card-header">
              <h3>League Structure</h3>
              <button type="button" className="btn-edit-section" onClick={() => goToStep(2)}>Edit</button>
            </div>
            <div className="review-card-body">
              {divisions.length === 0 ? (
                <p className="review-empty">No divisions added</p>
              ) : (
                <div className="review-structure">
                  {divisions.map(division => (
                    <div key={division.id} className="review-division">
                      <h4>{division.name || 'Unnamed Division'}</h4>
                      <ul className="review-teams-list">
                        {division.teams.map(team => (
                          <li key={team.id}>
                            <span className="team-bullet"></span>
                            {team.team_name || 'Unnamed Team'}
                            {team.sponsor_name && <span className="sponsor-note"> (Sponsor: {team.sponsor_name})</span>}
                          </li>
                        ))}
                        {division.teams.length === 0 && <li className="no-teams">No teams</li>}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Order Totals */}
          <div className="review-card totals-card">
            <div className="review-card-header">
              <h3>Order Summary</h3>
            </div>
            <div className="review-card-body">
              <div className="totals-grid">
                <div className="total-item">
                  <span className="total-label">Total Divisions</span>
                  <span className="total-value">{divisions.length}</span>
                </div>
                <div className="total-item">
                  <span className="total-label">Total Teams</span>
                  <span className="total-value">{divisions.reduce((acc, d) => acc + d.teams.length, 0)}</span>
                </div>
                <div className="total-item">
                  <span className="total-label">Total Items</span>
                  <span className="total-value">{totals.totalQuantity}</span>
                </div>
                <div className="total-item highlight">
                  <span className="total-label">Estimated Total</span>
                  <span className="total-value">${totals.totalPrice}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="order-creation-page">
      <div className="order-creation-container">
        <div className="page-header">
          <button onClick={handleCancel} className="btn-back">
            &#8592; Back to Dashboard
          </button>
          <h1>{isEditMode ? 'Edit Order' : 'Create New Order'}</h1>
          <p>{isEditMode ? `Editing order: ${editOrder.order_number}` : 'Follow the steps to create your order'}</p>
        </div>

        {/* Wizard Stepper */}
        {renderStepper()}

        <div className="order-form wizard-form">
          {/* Step Content */}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          {/* Navigation Buttons */}
          <div className="wizard-navigation">
            <div className="nav-left">
              {currentStep > 1 && (
                <button type="button" onClick={goToPrevStep} className="btn-nav btn-prev">
                  &#8592; Previous
                </button>
              )}
            </div>
            <div className="nav-right">
              {currentStep < 4 && (
                <button
                  key="next-button"
                  type="button"
                  onClick={goToNextStep}
                  className="btn-nav btn-next"
                  disabled={
                    (currentStep === 1 && !canProceedFromStep1()) ||
                    (currentStep === 2 && !canProceedFromStep2())
                  }
                >
                  Next &#8594;
                </button>
              )}
              {currentStep === 4 && (
                <button key="submit-button" type="button" onClick={handleSubmit} className="btn-nav btn-submit">
                  {isEditMode ? 'Update Order' : 'Create Order'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderCreation;
